import { z } from "zod";

// A redirect_uri must be a valid URL whose scheme is http or https — rejects javascript:/data:/file:.
// `.regex()` goes BEFORE `.url()`: that ordering keeps `format:"uri"` in the generated JSON Schema AND adds
// the scheme `pattern` (verified against zod 4.4.3; url-before-regex silently drops `format`). No `/i` flag —
// a JSON Schema `pattern` carries no case-insensitivity, so the rule is lowercase http/https only, identical
// on the client (safeParse) and the server ([ValidateJsonSchema]). The fuller policy (loopback-only http, no
// fragment) is enforced server-side in OAuthClientService.ValidateRedirectUris. Shared by the update schema
// and the dialog's add-time check so the scheme rule has a single source.
export const redirectUriSchema = z
  .string()
  .regex(/^https?:\/\//, "redirect_uri must use http or https")
  .url("Each redirect_uri must be a valid URL");

// Create an OAuth client. Cross-field rules (confidential ⇒ secret required, public ⇒ no secret / PKCE)
// are business logic enforced server-side — Zod covers only per-field shape. .max() values match the
// openiddict_applications columns so over-long input is a clean 400, not a Postgres VARCHAR 500.
export const createOAuthClientRequestSchema = z.object({
  client_id: z.string().min(1, "client_id is required").max(100, "Too long"), // client_id VARCHAR(100)
  application_type: z.enum(["web", "native"]).nullable().optional(),
  client_type: z.enum(["public", "confidential"]),
  display_name: z.string().max(200, "Too long").nullable().optional(), // display_name VARCHAR(200)
  client_secret: z.string().min(1).max(500).nullable().optional(), // client_secret VARCHAR(500)
  redirect_uris: z.array(redirectUriSchema).default([]),
  permissions: z.array(z.string()).nullable().optional(),
  // slug VARCHAR(50); non-blank when present — `\S` rejects whitespace-only (a blank slug would surface as a
  // bogus role-assignable product), mirroring the first_name/last_name convention.
  slug: z.string().min(1, "slug cannot be blank").max(50, "Too long").regex(/\S/, "slug cannot be blank").nullable().optional(),
});
